import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import {
  BookOpen,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Target,
  TrendingUp,
  Settings,
  ShieldAlert,
  BarChart3,
  Gamepad2,
  Zap,
} from 'lucide-react'

interface WordData {
  id: number
  word: string
  masteryLevel: number
  accuracy: number
  attempts: number
  streak: number
  lastSeen: string | null
  isLapsed: boolean
  needsReview: boolean
}

interface ContentSetData {
  id: number
  name: string
  description: string
  gradeLevel: number | null
  totalWords: number
  attempted: number
  mastered: number
  needsReview: number
  progressPct: number
  words: WordData[]
}

interface SpellingFocus {
  focusThrough: string | null
  currentGroup: string | null
}

interface SpellingProgressData {
  student: { id: number; name: string }
  contentSets: ContentSetData[]
  spellingFocus: SpellingFocus | null
  summary: {
    totalWords: number
    masteredWords: number
    needsReview: number
    overallProgress: number
  }
}

interface PredictedScoreData {
  student: { id: number; name: string }
  predictedScore: number
  confidence: number
  confidenceLabel: string
  throughListGroup: string
  totalWords: number
  wordsPracticed: number
  wordsNotPracticed: number
  levelDistribution: Record<number, number>
  expectedCorrect: number
  wordsNeedingWork: {
    id: number
    word: string
    listGroup: string
    masteryLevel: number
    accuracy: number
    attempts: number
    currentProb: number
    potentialGain: number
  }[]
}

interface GameStats {
  name: string
  modality: string
  totalAttempts: number
  totalCorrect: number
  accuracy: number
  uniqueWords: number
  avgResponseMs: number
  levelUps: number
  avgAttemptsToLevel3: number | null
}

interface GameRecommendation {
  recommendedGame: string
  recommendedGameName: string
  reason: string
  alternatives: { game: string; gameName: string; reason: string }[]
  basedOnData: boolean
  recentGames: string[]
}

interface GameEffectivenessData {
  student: { id: number; name: string }
  effectiveness: {
    games: Record<string, GameStats>
    totalAttempts: number
    totalSessions: number
    dataAge: string
  }
  recommendation: GameRecommendation
}

interface CalibrationRecord {
  testNum: number
  testScope: string
  actualScore: number
  predictedScore: number
  delta: number
  confidence: number
  confidenceLabel: string
  wordsPracticed: number
  totalWords: number
}

interface CalibrationData {
  student: { id: number; name: string }
  calibrations: CalibrationRecord[]
  summary: {
    count: number
    avgDelta: number
    avgAbsDelta: number
    accuracy: string
    trend: string
  }
}

interface RiskGroup {
  group: string
  risk: string
  riskScore: number
  evidence: string
  testNum: number
  scoreAt: number
  scoreBefore: number | null
  delta: number
}

interface RiskGroupsData {
  student: { id: number; name: string }
  riskGroups: RiskGroup[]
  testScores: { testNum: number; score: number }[]
}

// Ordered list groups for the curriculum
const LIST_GROUPS = [
  '1-5', '6-10', '11-15', '16-20', '21-25', '26-30', '31-35', '36-40',
  '41-45', '46-50', '51-55', '56-60', '61-65', '66-70', '71-75', '76-80',
  '81-85', '86-90', '91-95', '96-100', '101-105', '106-110', '111-115',
]

// Focus-through options (test scope ends)
const FOCUS_OPTIONS = LIST_GROUPS.map(g => {
  const end = g.split('-')[1]
  return { value: `1-${end}`, label: `Lists 1-${end}`, group: g }
})

function getMasteryColor(level: number): string {
  if (level >= 5) return 'text-green-500'
  if (level >= 4) return 'text-blue-500'
  if (level >= 3) return 'text-yellow-500'
  if (level >= 1) return 'text-orange-500'
  return 'text-gray-500'
}

function getMasteryBg(level: number): string {
  if (level >= 5) return 'bg-green-500/20'
  if (level >= 4) return 'bg-blue-500/20'
  if (level >= 3) return 'bg-yellow-500/20'
  if (level >= 1) return 'bg-orange-500/20'
  return 'bg-gray-500/20'
}

function getMasteryLabel(level: number): string {
  if (level >= 5) return 'Mastered'
  if (level >= 4) return 'Proficient'
  if (level >= 3) return 'Practiced'
  if (level >= 2) return 'Familiar'
  if (level >= 1) return 'Learning'
  return 'New'
}

interface Props {
  studentId: number
  studentName: string
}

export default function SpellingProgress({ studentId }: Props) {
  const [expandedSet, setExpandedSet] = useState<number | null>(null)
  const [showFocusSettings, setShowFocusSettings] = useState(false)
  const [focusThrough, setFocusThrough] = useState('')
  const [currentGroup, setCurrentGroup] = useState('')
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery<SpellingProgressData>({
    queryKey: ['spelling-progress', studentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${studentId}/spelling`)
      return response.data
    },
  })

  // Initialize focus settings from server data
  const serverFocus = data?.spellingFocus
  const displayFocusThrough = focusThrough || serverFocus?.focusThrough || ''
  const displayCurrentGroup = currentGroup || serverFocus?.currentGroup || ''

  const saveFocusMutation = useMutation({
    mutationFn: async (payload: { focusThrough: string; currentGroup: string }) => {
      const response = await api.put(
        `/api/parent/students/${studentId}/spelling/focus`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spelling-progress', studentId] })
      setShowFocusSettings(false)
      setFocusThrough('')
      setCurrentGroup('')
    },
  })

  const [showWeakWords, setShowWeakWords] = useState(false)

  const { data: predictionData } = useQuery<PredictedScoreData>({
    queryKey: ['spelling-predicted-score', studentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${studentId}/spelling/predicted-score`)
      return response.data
    },
  })

  const { data: gameData } = useQuery<GameEffectivenessData>({
    queryKey: ['spelling-game-effectiveness', studentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${studentId}/spelling/game-effectiveness`)
      return response.data
    },
  })

  const { data: calibrationData } = useQuery<CalibrationData>({
    queryKey: ['spelling-calibration', studentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${studentId}/spelling/calibration`)
      return response.data
    },
  })

  const { data: riskData } = useQuery<RiskGroupsData>({
    queryKey: ['spelling-risk-groups', studentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/students/${studentId}/spelling/risk-groups`)
      return response.data
    },
  })

  const highRiskGroups = riskData?.riskGroups.filter(g => g.riskScore >= 0.5) || []

  // Get the list groups within the current focus scope for the "This Week" dropdown
  const focusThroughEnd = displayFocusThrough
    ? parseInt(displayFocusThrough.split('-')[1] || '0')
    : 0
  const groupsInScope = LIST_GROUPS.filter(g => {
    const end = parseInt(g.split('-')[1] || '0')
    return end <= focusThroughEnd
  })

  if (isLoading) {
    return (
      <div className="card animate-pulse">
        <div className="h-6 w-48 bg-gray-700 rounded mb-4"></div>
        <div className="h-32 bg-gray-700 rounded"></div>
      </div>
    )
  }

  if (error || !data) {
    return null
  }

  if (data.contentSets.length === 0) {
    return (
      <div className="card">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-500" />
          Spelling Progress
        </h2>
        <p className="text-gray-400 text-center py-8">
          No spelling practice yet. Start Spell Quest in the Game Arcade!
        </p>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-500" />
          Spelling Progress
        </h2>
        <button
          onClick={() => setShowFocusSettings(!showFocusSettings)}
          className="p-2 rounded-lg hover:bg-surface-hover transition-colors"
          title="Test Scope Settings"
        >
          <Settings className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Spelling Focus Config */}
      {serverFocus?.focusThrough && !showFocusSettings && (
        <div className="mb-4 px-3 py-2 bg-purple-500/10 border border-purple-500/30 rounded-lg text-sm">
          <span className="text-purple-400">Test scope:</span>{' '}
          <span className="text-gray-300">Lists {serverFocus.focusThrough}</span>
          {serverFocus.currentGroup && (
            <>
              <span className="text-gray-600 mx-2">|</span>
              <span className="text-purple-400">This week:</span>{' '}
              <span className="text-gray-300">Lists {serverFocus.currentGroup}</span>
            </>
          )}
        </div>
      )}

      {showFocusSettings && (
        <div className="mb-4 p-4 bg-surface-card/50 border border-gray-700/50 rounded-lg space-y-3">
          <div className="text-sm font-medium text-gray-300 mb-2">Test Scope Settings</div>

          <div>
            <label className="text-xs text-gray-500 block mb-1">
              Test covers through:
            </label>
            <select
              value={displayFocusThrough}
              onChange={(e) => setFocusThrough(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-200"
            >
              <option value="">Select test scope...</option>
              {FOCUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-500 block mb-1">
              This week's new words:
            </label>
            <select
              value={displayCurrentGroup}
              onChange={(e) => setCurrentGroup(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-200"
            >
              <option value="">Select current week...</option>
              {groupsInScope.map(g => (
                <option key={g} value={g}>
                  Lists {g}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={() => {
                const ft = focusThrough || serverFocus?.focusThrough
                const cg = currentGroup || serverFocus?.currentGroup
                if (ft) {
                  saveFocusMutation.mutate({
                    focusThrough: ft,
                    currentGroup: cg || '',
                  })
                }
              }}
              disabled={saveFocusMutation.isPending || (!focusThrough && !currentGroup)}
              className="px-4 py-1.5 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
            >
              {saveFocusMutation.isPending ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => {
                setShowFocusSettings(false)
                setFocusThrough('')
                setCurrentGroup('')
              }}
              className="px-4 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
          {saveFocusMutation.isError && (
            <p className="text-red-400 text-xs">Failed to save. Please try again.</p>
          )}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-3 bg-surface-card/50 rounded-lg">
          <div className="text-2xl font-bold text-green-500">{data.summary.masteredWords}</div>
          <div className="text-xs text-gray-500">Mastered</div>
        </div>
        <div className="text-center p-3 bg-surface-card/50 rounded-lg">
          <div className="text-2xl font-bold text-purple-500">{data.summary.totalWords}</div>
          <div className="text-xs text-gray-500">Total Words</div>
        </div>
        <div className="text-center p-3 bg-surface-card/50 rounded-lg">
          <div className="text-2xl font-bold text-orange-500">{data.summary.needsReview}</div>
          <div className="text-xs text-gray-500">Need Review</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-400">Overall Mastery</span>
          <span className="text-green-500 font-medium">{data.summary.overallProgress}%</span>
        </div>
        <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-green-500 transition-all duration-500"
            style={{ width: `${data.summary.overallProgress}%` }}
          />
        </div>
      </div>

      {/* Predicted Test Score */}
      {predictionData && predictionData.totalWords > 0 && (
        <div className="mb-6 p-4 bg-surface-card/50 border border-gray-700/50 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-gray-200">
              Predicted Test Score
            </span>
            <span className="text-xs text-gray-500 ml-auto">
              Lists {predictionData.throughListGroup}
            </span>
          </div>

          {/* Score + Confidence */}
          <div className="flex items-end gap-4 mb-4">
            <div>
              <div className={`text-3xl font-bold ${
                predictionData.predictedScore >= 90 ? 'text-green-400' :
                predictionData.predictedScore >= 75 ? 'text-blue-400' :
                predictionData.predictedScore >= 60 ? 'text-yellow-400' :
                'text-red-400'
              }`}>
                {predictionData.predictedScore}%
              </div>
              <div className="text-xs text-gray-500">estimated</div>
            </div>
            <div className="flex-1">
              <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 rounded-full ${
                    predictionData.predictedScore >= 90 ? 'bg-green-500' :
                    predictionData.predictedScore >= 75 ? 'bg-blue-500' :
                    predictionData.predictedScore >= 60 ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${predictionData.predictedScore}%` }}
                />
              </div>
            </div>
            <div className="text-right">
              <div className={`text-xs px-2 py-0.5 rounded-full ${
                predictionData.confidenceLabel === 'high' ? 'bg-green-500/20 text-green-400' :
                predictionData.confidenceLabel === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-gray-600/40 text-gray-400'
              }`}>
                {predictionData.confidenceLabel === 'none' ? 'no data' : predictionData.confidenceLabel} confidence
              </div>
            </div>
          </div>

          {/* Mastery Distribution Bar */}
          <div className="mb-3">
            <div className="text-xs text-gray-500 mb-1">
              {predictionData.wordsPracticed} of {predictionData.totalWords} words practiced
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden flex">
              {[5, 4, 3, 2, 1, 0].map(level => {
                const count = predictionData.levelDistribution[level] || 0
                const pct = (count / predictionData.totalWords) * 100
                if (pct === 0) return null
                const colors: Record<number, string> = {
                  5: 'bg-green-500', 4: 'bg-blue-500', 3: 'bg-yellow-500',
                  2: 'bg-orange-500', 1: 'bg-red-400', 0: 'bg-gray-600',
                }
                return (
                  <div
                    key={level}
                    className={`h-full ${colors[level]}`}
                    style={{ width: `${pct}%` }}
                    title={`Level ${level}: ${count} words`}
                  />
                )
              })}
            </div>
            <div className="flex justify-between text-xs text-gray-600 mt-0.5">
              <span>Mastered</span>
              <span>New</span>
            </div>
          </div>

          {/* Words Needing Work Toggle */}
          {predictionData.wordsNeedingWork.length > 0 && (
            <div>
              <button
                onClick={() => setShowWeakWords(!showWeakWords)}
                className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
              >
                {showWeakWords ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {predictionData.wordsNeedingWork.length} words need more practice
              </button>

              {showWeakWords && (
                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1.5">
                  {predictionData.wordsNeedingWork.slice(0, 15).map((w) => (
                    <div
                      key={w.id}
                      className="flex items-center justify-between px-2 py-1 bg-gray-700/30 rounded text-xs"
                    >
                      <span className="font-medium text-gray-200 truncate">{w.word}</span>
                      <span className={`ml-1 flex-shrink-0 ${
                        w.masteryLevel === 0 ? 'text-gray-500' :
                        w.masteryLevel <= 2 ? 'text-orange-400' :
                        'text-yellow-400'
                      }`}>
                        L{w.masteryLevel}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Prediction Accuracy */}
      {calibrationData && calibrationData.calibrations.length > 0 && (
        <div className="mb-6 p-4 bg-surface-card/50 border border-gray-700/50 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-gray-200">
              Prediction Accuracy
            </span>
            <span className="text-xs text-gray-500 ml-auto">
              {calibrationData.summary.count} tests tracked
            </span>
          </div>

          {/* Summary */}
          <div className="mb-3 text-sm">
            <span className="text-gray-400">Our predictions have been </span>
            <span className={`font-medium ${
              calibrationData.summary.avgAbsDelta <= 10 ? 'text-green-400' :
              calibrationData.summary.avgAbsDelta <= 20 ? 'text-yellow-400' :
              'text-gray-400'
            }`}>
              {calibrationData.summary.accuracy}
            </span>
          </div>

          {/* Mini chart: actual vs predicted per test */}
          <div className="space-y-1.5">
            {calibrationData.calibrations.map((cal) => (
              <div key={cal.testNum} className="flex items-center gap-2 text-xs">
                <span className="text-gray-500 w-12 text-right">T{cal.testNum}</span>
                <div className="flex-1 relative h-4 bg-gray-700 rounded overflow-hidden">
                  {/* Actual score bar */}
                  <div
                    className="absolute top-0 left-0 h-full bg-cyan-500/40 rounded"
                    style={{ width: `${cal.actualScore}%` }}
                  />
                  {/* Predicted marker */}
                  {cal.predictedScore > 0 && cal.wordsPracticed > 0 && (
                    <div
                      className="absolute top-0 h-full w-0.5 bg-yellow-400"
                      style={{ left: `${cal.predictedScore}%` }}
                      title={`Predicted: ${cal.predictedScore}%`}
                    />
                  )}
                </div>
                <span className="text-gray-300 w-10 text-right">{cal.actualScore}%</span>
                <span className={`w-12 text-right ${
                  cal.delta > 0 ? 'text-green-400' : cal.delta < -10 ? 'text-red-400' : 'text-gray-500'
                }`}>
                  {cal.delta > 0 ? '+' : ''}{cal.delta}
                </span>
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex gap-4 mt-2 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-2 bg-cyan-500/40 rounded" /> Actual
            </div>
            <div className="flex items-center gap-1">
              <div className="w-0.5 h-2 bg-yellow-400" /> Predicted
            </div>
            <div className="flex items-center gap-1">
              <span className="text-green-400">+</span> Beat prediction
            </div>
          </div>

          {calibrationData.summary.trend !== 'not enough data' && (
            <div className="mt-2 text-xs text-gray-500">
              Trend: <span className={
                calibrationData.summary.trend === 'improving' ? 'text-green-400' :
                calibrationData.summary.trend === 'worsening' ? 'text-red-400' :
                'text-gray-400'
              }>{calibrationData.summary.trend}</span>
            </div>
          )}
        </div>
      )}

      {/* High-Risk Groups */}
      {highRiskGroups.length > 0 && (
        <div className="mb-6 p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium text-red-400">
              High-Risk Word Groups
            </span>
          </div>
          <div className="space-y-1.5">
            {highRiskGroups.map((g) => (
              <div key={g.group} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    g.risk === 'critical'
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-orange-500/20 text-orange-400'
                  }`}>
                    {g.risk}
                  </span>
                  <span className="text-gray-300">Lists {g.group}</span>
                </div>
                <span className="text-gray-500 text-xs">{g.evidence}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Game Insights */}
      {gameData && (
        <div className="mb-6 p-4 bg-surface-card/50 border border-gray-700/50 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Gamepad2 className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-medium text-gray-200">
              Game Insights
            </span>
            {gameData.effectiveness.dataAge !== 'no data' && (
              <span className="text-xs text-gray-500 ml-auto">
                Last played: {gameData.effectiveness.dataAge}
              </span>
            )}
          </div>

          {/* Next Game Recommendation */}
          <div className="mb-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-sm font-medium text-emerald-300">
                Play Next: {gameData.recommendation.recommendedGameName}
              </span>
            </div>
            <p className="text-xs text-gray-400 ml-5">
              {gameData.recommendation.reason}
            </p>
            {gameData.recommendation.alternatives.length > 0 && (
              <p className="text-xs text-gray-500 ml-5 mt-1">
                Also try: {gameData.recommendation.alternatives.map(a => a.gameName).join(', ')}
              </p>
            )}
          </div>

          {/* Per-Game Stats (only if data exists) */}
          {Object.keys(gameData.effectiveness.games).length > 0 ? (
            <div className="space-y-2">
              <div className="text-xs text-gray-500 mb-1">
                {gameData.effectiveness.totalAttempts} total attempts across {gameData.effectiveness.totalSessions} sessions
              </div>
              {Object.entries(gameData.effectiveness.games)
                .sort(([, a], [, b]) => b.totalAttempts - a.totalAttempts)
                .map(([gameId, stats]) => (
                  <div key={gameId} className="flex items-center gap-3 text-sm">
                    <span className="text-gray-300 w-28 truncate font-medium">{stats.name}</span>
                    <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          stats.accuracy >= 85 ? 'bg-green-500' :
                          stats.accuracy >= 70 ? 'bg-blue-500' :
                          stats.accuracy >= 50 ? 'bg-yellow-500' :
                          'bg-red-400'
                        }`}
                        style={{ width: `${stats.accuracy}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-400 w-10 text-right">{stats.accuracy}%</span>
                    <span className="text-xs text-gray-500 w-16 text-right">{stats.totalAttempts} tries</span>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 text-center py-2">
              No spelling game data yet. Play some games to see which ones work best!
            </p>
          )}
        </div>
      )}

      {/* Content Sets */}
      <div className="space-y-3">
        {data.contentSets.map((set) => (
          <div key={set.id} className="border border-gray-700/50 rounded-lg overflow-hidden">
            {/* Set Header */}
            <button
              onClick={() => setExpandedSet(expandedSet === set.id ? null : set.id)}
              className="w-full p-4 flex items-center justify-between hover:bg-surface-hover transition-colors"
            >
              <div className="flex items-center gap-3">
                {expandedSet === set.id ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
                <div className="text-left">
                  <div className="font-medium">{set.name}</div>
                  <div className="text-sm text-gray-500">
                    {set.mastered}/{set.totalWords} mastered
                    {set.needsReview > 0 && (
                      <span className="text-orange-500 ml-2">
                        ({set.needsReview} need review)
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500"
                    style={{ width: `${set.progressPct}%` }}
                  />
                </div>
                <span className="text-sm text-gray-400 w-12 text-right">
                  {set.progressPct}%
                </span>
              </div>
            </button>

            {/* Expanded Word List */}
            {expandedSet === set.id && (
              <div className="border-t border-gray-700/50 p-4 bg-surface-card/30">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {set.words.map((word) => (
                    <div
                      key={word.id}
                      className={`flex items-center justify-between p-2 rounded-lg ${getMasteryBg(
                        word.masteryLevel
                      )}`}
                    >
                      <div className="flex items-center gap-2">
                        {word.masteryLevel >= 5 ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : word.isLapsed || word.needsReview ? (
                          <AlertCircle className="w-4 h-4 text-orange-500" />
                        ) : word.attempts === 0 ? (
                          <Target className="w-4 h-4 text-gray-500" />
                        ) : (
                          <TrendingUp className={`w-4 h-4 ${getMasteryColor(word.masteryLevel)}`} />
                        )}
                        <span className="font-medium">{word.word}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        {word.attempts > 0 && (
                          <>
                            <span className={getMasteryColor(word.masteryLevel)}>
                              {getMasteryLabel(word.masteryLevel)}
                            </span>
                            <span className="text-gray-500">{word.accuracy}%</span>
                            {word.streak > 0 && (
                              <span className="text-orange-500">{word.streak}x</span>
                            )}
                          </>
                        )}
                        {word.attempts === 0 && (
                          <span className="text-gray-500">Not started</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Legend */}
                <div className="mt-4 pt-4 border-t border-gray-700/50 flex flex-wrap gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-green-500" /> Mastered
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-blue-500" /> Learning
                  </div>
                  <div className="flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 text-orange-500" /> Needs Review
                  </div>
                  <div className="flex items-center gap-1">
                    <Target className="w-3 h-3 text-gray-500" /> Not Started
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
